import { createContext, FC, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Box, BoxProps, rem, Skeleton, StyleProps, Td, Tr, useColorModeValue } from '@bonusly/design-system';

// Create the context within this module
const OverflowContext = createContext<boolean | undefined>(undefined);

// Hook to use the overflow context
export const useOverflowContext = () => {
	const context = useContext(OverflowContext);
	if (context === undefined) {
		throw new Error('useOverflowContext must be used within a MobileFriendlyTableWrapper');
	}
	return context;
};

interface MobileFriendlyTableWrapperProps extends BoxProps {
	children: React.ReactNode;
}

export const MobileFriendlyTableWrapper = ({ children, ...styleProps }: MobileFriendlyTableWrapperProps) => {
	const [hasOverflow, setHasOverflow] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const leftFadeRef = useRef<HTMLDivElement>(null);
	const rightFadeRef = useRef<HTMLDivElement>(null);
	// Mirror hasOverflow as a ref so updateScrollState can avoid calling
	// setHasOverflow on every scroll tick when the value hasn't changed.
	const hasOverflowRef = useRef(false);
	const rightFadeGradient = useColorModeValue(
		'linear-gradient(90deg, rgba(221, 221, 221, 0) 0%, rgba(221, 221, 221, 0.5) 100%)',
		'linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%)'
	);
	const leftFadeGradient = useColorModeValue(
		'linear-gradient(270deg, rgba(221, 221, 221, 0) 0%, rgba(221, 221, 221, 0.5) 100%)',
		'linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%)'
	);

	// Drives fade visibility imperatively via refs to avoid a React render on
	// every scroll tick. Keeping this off the render path eliminates the
	// commit-cycle delay that made the shadow lag a frame or two behind the
	// scroll position. setHasOverflow only fires when overflow status flips,
	// so OverflowContext consumers (StickyTd) still update correctly.
	const updateScrollState = useCallback(() => {
		const el = containerRef.current;
		const leftFade = leftFadeRef.current;
		const rightFade = rightFadeRef.current;
		if (!el || !leftFade || !rightFade) return;
		const { scrollLeft, scrollWidth, clientWidth } = el;
		const overflow = scrollWidth > clientWidth;
		// 1px tolerance to absorb subpixel rounding when at the right edge.
		const atRightEdge = scrollLeft + clientWidth >= scrollWidth - 1;
		leftFade.style.display = overflow && scrollLeft > 0 ? 'block' : 'none';
		rightFade.style.display = overflow && !atRightEdge ? 'block' : 'none';
		if (overflow !== hasOverflowRef.current) {
			hasOverflowRef.current = overflow;
			setHasOverflow(overflow);
		}
	}, []);

	useEffect(() => {
		const timeoutId = setTimeout(updateScrollState, 100);

		window.addEventListener('resize', updateScrollState);

		const observer = new MutationObserver(() => {
			setTimeout(updateScrollState, 10);
		});
		if (containerRef.current) {
			observer.observe(containerRef.current, { childList: true, subtree: true });
		}

		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener('resize', updateScrollState);
			observer.disconnect();
		};
	}, [updateScrollState]);

	return (
		<OverflowContext.Provider value={hasOverflow}>
			<Box position="relative" {...styleProps}>
				<Box
					ref={leftFadeRef}
					position="absolute"
					top={0}
					left={0}
					height="100%"
					width="20px"
					background={leftFadeGradient}
					pointerEvents="none"
					// Sit beneath any sticky first column (StickyTd uses zIndex 1+),
					// so the sticky column hides this fade when one is present.
					// Tables without a sticky column (e.g. Bizy) still get the fade
					// because it sits above non-positioned table content.
					zIndex={0}
					display="none"
				/>
				<Box
					ref={rightFadeRef}
					position="absolute"
					top={0}
					right={0}
					height="100%"
					width="20px"
					background={rightFadeGradient}
					pointerEvents="none"
					zIndex={3}
					display="none"
				/>
				<Box ref={containerRef} overflowX="auto" onScroll={updateScrollState}>
					{children}
				</Box>
			</Box>
		</OverflowContext.Provider>
	);
};

export const StickyTd = (props: React.PropsWithChildren<StyleProps & { _hover?: any; as?: React.ElementType; rowSpan?: number; colSpan?: number }>) => {
	const { _hover, ...restProps } = props;
	const hasOverflow = useOverflowContext();
	const stickyGradient = useColorModeValue(
		'linear-gradient(270deg, rgba(221, 221, 221, 0) 0%, rgba(221, 221, 221, 0.5) 100%)',
		'linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%)'
	);

	return (
		<Td
			position="sticky"
			left={0}
			bg="bg.surface"
			zIndex={1}
			_after={{
				content: hasOverflow ? '""' : 'none',
				position: 'absolute',
				top: 0,
				right: '-20px',
				bottom: 0,
				width: '20px',
				background: stickyGradient,
				pointerEvents: 'none',
			}}
			_hover={{ ..._hover }}
			{...restProps}
		/>
	);
};

interface SkeletonTableRowsProps extends StyleProps {
	numberOfRows: number;
	numberOfColumns: number;
	sx?: any;
}

export const SkeletonTableRows: FC<SkeletonTableRowsProps> = ({ numberOfRows, numberOfColumns, sx, ...props }) => {
	return (
		<>
			{[...Array(numberOfRows).keys()].map((index) => {
				return (
					<Tr key={index} bgColor="bg.surface" sx={sx} _last={{ '> td': { borderBottom: 'none' } }} {...props}>
						<StickyTd>
							<Skeleton h={rem(30)} w="100%" />
						</StickyTd>
						{[...Array(numberOfColumns - 1).keys()].map((index) => {
							return (
								<Td key={index} bgColor="bg.surface">
									<Skeleton h={rem(30)} w="100%" />
								</Td>
							);
						})}
					</Tr>
				);
			})}
		</>
	);
};
